import { supabase } from './supabase';

interface EvolutionApiConfig {
  url: string;
  apikey: string;
}

interface InfraestruturaChatbot {
  id?: number;
  rotaapi: string;
  codempresa: number;
  tipo_integracao: string;
  api_whatsapp: any;
  apikey_instance: string;
  instance_name: string;
}

interface MensagemParams {
  telefone: string;
  mensagem: string;
}

interface CreateInstanceParams {
  instanceName: string;
  numero: string;
  codempresa: number;
}

interface CreateInstanceResponse {
  success: boolean;
  message: string;
  instanceData?: any;
}

export interface QRCodeResponse {
  pairingCode: string | null;
  code: string;
  base64: string;
  count: number;
}

export const evolutionApiService = {
  // Cache para evitar múltiplas chamadas
  _instancesCache: null as any,
  _cacheTimestamp: 0,
  _cacheDuration: 30000, // 30 segundos

  /**
   * Busca TODAS as instâncias uma única vez e cacheia
   */
  async buscarTodasInstancias(): Promise<any[]> {
    try {
      // Verificar cache
      const now = Date.now();
      if (this._instancesCache && (now - this._cacheTimestamp) < this._cacheDuration) {
        console.log('➡️ Usando cache de instâncias');
        return this._instancesCache;
      }

      console.log('🔄 Buscando TODAS as instâncias da API (cache expirado ou vazio)');
      
      // Obter configuração global da Evolution API
      const config = await this.getConfig();
      
      if (!config || !config.url || !config.apikey) {
        console.error('Configuração da Evolution API não encontrada');
        return [];
      }
      
      // Construir URL
      let baseUrl = config.url;
      baseUrl = baseUrl.replace(/\/$/, '');
      const url = `${baseUrl}/instance/fetchInstances`;
      
      console.log('URL:', url);
      
      // Buscar instâncias
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        }
      });
      
      if (!response.ok) {
        console.error(`Erro ao buscar instâncias: ${response.status} ${response.statusText}`);
        return [];
      }
      
      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Erro ao converter resposta para JSON:', e);
        return [];
      }
      
      // A resposta é um array direto de instâncias
      if (Array.isArray(responseData)) {
        console.log(`✓ ${responseData.length} instância(s) encontradas na API`);
        // Atualizar cache
        this._instancesCache = responseData;
        this._cacheTimestamp = now;
        return responseData;
      }
      // Fallback para formato antigo
      else if (responseData && responseData.instances && Array.isArray(responseData.instances)) {
        console.log(`✓ ${responseData.instances.length} instância(s) encontradas na API`);
        this._instancesCache = responseData.instances;
        this._cacheTimestamp = now;
        return responseData.instances;
      }
      
      return [];
    } catch (err) {
      console.error('Erro ao buscar instâncias:', err);
      return [];
    }
  },

  /**
   * Busca os detalhes de uma instância específica usando o parâmetro instanceName
   * @param instanceName Nome da instância a buscar
   */
  async buscarDetalhesInstancia(instanceName: string): Promise<any> {
    try {
      console.log(`🔍 Buscando instância específica: ${instanceName}`);
      
      // Obter configuração global da Evolution API
      const config = await this.getConfig();
      
      if (!config || !config.url || !config.apikey) {
        console.error('Configuração da Evolution API não encontrada');
        return null;
      }
      
      // Construir URL com parâmetro instanceName
      let baseUrl = config.url;
      baseUrl = baseUrl.replace(/\/$/, '');
      const url = `${baseUrl}/instance/fetchInstances?instanceName=${encodeURIComponent(instanceName)}`;
      
      console.log('URL:', url);
      
      // Buscar instância específica
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`⚠️ Instância ${instanceName} não encontrada na API Evolution (404)`);
          return { notFound: true, instanceName };
        }
        console.error(`Erro ao buscar instância: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Erro ao converter resposta para JSON:', e);
        return null;
      }
      
      // A resposta com parâmetro retorna um array com uma instância ou vazio
      if (Array.isArray(responseData) && responseData.length > 0) {
        console.log(`✓ Instância ${instanceName} encontrada`);
        return responseData[0];
      }
      
      console.log(`✗ Instância ${instanceName} NÃO encontrada`);
      return null;
    } catch (err) {
      console.error('Erro ao buscar detalhes da instância:', err);
      return null;
    }
  },

  /**
   * Limpa o cache de instâncias
   */
  limparCache() {
    console.log('🗑️ Limpando cache de instâncias');
    this._instancesCache = null;
    this._cacheTimestamp = 0;
  },
  /**
   * Verifica se uma instância já existe
   * @param instanceName Nome da instância a verificar
   */
  async verificarInstanciaExiste(instanceName: string): Promise<boolean> {
    try {
      console.log('Verificando se a instância já existe:', instanceName);
      
      // Obter configuração global da Evolution API
      const config = await this.getConfig();
      
      if (!config || !config.url || !config.apikey) {
        console.error('Configuração da Evolution API não encontrada');
        return false;
      }
      
      // Construir URL para verificar o estado da conexão da instância
      let baseUrl = config.url;
      baseUrl = baseUrl.replace(/\/$/, '');
      
      // Usar o endpoint connection-state para verificar se a instância existe
      const urlConnectionState = `${baseUrl}/instance/connectionState/${instanceName}`;
      
      console.log('URL para verificar estado da conexão:', urlConnectionState);
      
      // Verificar estado da conexão
      const response = await fetch(urlConnectionState, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        }
      });
      
      const responseText = await response.text();
      console.log('Resposta da verificação de conexão:', responseText);
      
      // Se a resposta for 200, a instância existe (mesmo que não esteja conectada)
      if (response.ok) {
        let responseData;
        
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          console.error('Erro ao converter resposta para JSON:', e);
          return false;
        }
        
        // Se a resposta contiver dados sobre a instância, ela existe
        if (responseData && responseData.instance) {
          console.log(`Instância ${instanceName} existe! Estado:`, responseData.instance.state || 'desconhecido');
          return true;
        }
      }
      
      // Se o erro for 404, a instância não existe
      if (response.status === 404) {
        console.log(`Instância ${instanceName} NÃO existe!`);
        return false;
      }
      
      // Se a resposta contiver mensagem de erro indicando que a instância já existe
      if (responseText.includes('already in use')) {
        console.log(`Instância ${instanceName} já existe (detectado pela mensagem de erro)!`);
        return true;
      }
      
      // Verificar se a resposta contém a mensagem de erro específica
      try {
        const errorData = JSON.parse(responseText);
        if (errorData && errorData.response && errorData.response.message) {
          const messages = Array.isArray(errorData.response.message) 
            ? errorData.response.message 
            : [errorData.response.message];
            
          if (messages.some((msg: any) => typeof msg === 'string' && msg.includes('already in use'))) {
            console.log(`Instância ${instanceName} já existe (detectado pela mensagem de erro)!`);
            return true;
          }
        }
      } catch (e) {
        // Ignorar erros de parsing
      }
      
      console.log(`Instância ${instanceName} NÃO encontrada!`);
      return false;
    } catch (err) {
      console.error('Erro ao verificar se instância existe:', err);
      return false;
    }
  },
  /**
   * Busca a configuração da Evolution API
   * @param codempresa ID da empresa (não utilizado, configuração é global)
   */
  async getConfig(codempresa?: number): Promise<EvolutionApiConfig | null> {
    try {
      console.log('Buscando configuração global da Evolution API');
      
      // Buscar configuração da Evolution API na tabela config_geral (configuração global)
      const { data, error } = await supabase
        .from('config_geral')
        .select('*')
        .eq('nome_integracao', 'evolutionapi')
        .limit(1);

      if (error) {
        console.error('Erro ao buscar configuração da Evolution API:', error);
        return null;
      }

      if (!data || data.length === 0 || !data[0].dados) {
        console.error('Configuração da Evolution API não encontrada');
        return null;
      }

      const configData = data[0].dados;
      return {
        url: configData.url || '',
        apikey: configData.apikey || ''
      };
    } catch (err) {
      console.error('Erro ao buscar configuração da Evolution API:', err);
      return null;
    }
  },

  /**
   * Busca a infraestrutura do chatbot para SmartCobrança
   */
  async getInfraestruturaChatbot(): Promise<InfraestruturaChatbot | null> {
    try {
      // Buscar infraestrutura do chatbot para SmartCobrança
      const { data, error } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('tipo_integracao', 'smartcobranca')
        .limit(1);

      if (error) {
        console.error('Erro ao buscar infraestrutura do chatbot:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.error('Infraestrutura do chatbot não encontrada');
        return null;
      }

      return data[0];
    } catch (err) {
      console.error('Erro ao buscar infraestrutura do chatbot:', err);
      return null;
    }
  },

  /**
   * Busca o modelo de mensagem para cobrança
   */
  async getModeloMensagem(): Promise<string | null> {
    try {
      // Buscar modelo de mensagem para cobrança
      const { data, error } = await supabase
        .from('modelomensagem')
        .select('*')
        .eq('tipo', 'cobranca')
        .limit(1);

      if (error) {
        console.error('Erro ao buscar modelo de mensagem:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.error('Modelo de mensagem não encontrado');
        return null;
      }

      return data[0].modelo || '';
    } catch (err) {
      console.error('Erro ao buscar modelo de mensagem:', err);
      return null;
    }
  },

  /**
   * Cria uma nova instância na Evolution API
   * @param params Parâmetros para criação da instância
   * @returns Resposta da criação da instância
   */
  async criarInstancia(params: { instanceName: string; numero: string; codempresa: number }) {
    try {
      // Verificar se a instância já existe
      console.log('Verificando se a instância existe antes de criar:', params.instanceName);
      const instanciaExiste = await this.verificarInstanciaExiste(params.instanceName);
      
      if (instanciaExiste) {
        console.log('INSTÂNCIA JÁ EXISTE, não será criada novamente:', params.instanceName);
        
        // Verificar se a instância já existe na tabela infraestrutura_chatbot
        const { data: existingData, error: existingError } = await supabase
          .from('infraestrutura_chatbot')
          .select('*')
          .eq('instance_name', params.instanceName)
          .limit(1);
          
        if (existingData && existingData.length > 0) {
          console.log('Instância já existe na tabela infraestrutura_chatbot:', params.instanceName);
          return {
            success: true,
            message: 'Instância já existe e já está registrada no banco de dados',
            instanceData: null
          };
        }
        
        // Buscar rota da empresa
        const { data: empresaData, error: empresaError } = await supabase
          .from('empresa')
          .select('rota')
          .eq('id', params.codempresa)
          .single();
        
        const rotaapi = empresaData?.rota || '';
        
        // Buscar configuração global para preencher os dados
        const config = await this.getConfig();
        
        if (!config || !config.url || !config.apikey) {
          return {
            success: true,
            message: 'Instância já existe, mas não foi possível obter configuração da API',
            instanceData: null
          };
        }
        
        // Formatar número (remover caracteres especiais)
        const numeroFormatado = params.numero.replace(/\D/g, '');
        
        // Buscar detalhes da instância para obter informações completas
        console.log('Buscando detalhes da instância existente:', params.instanceName);
        const detalhesInstancia = await this.buscarDetalhesInstancia(params.instanceName);
        
        // Montar objeto api_whatsapp no formato correto
        const api_whatsapp = {
          id: 'smartcobranca', // ID fixo conforme solicitado
          owner: `${numeroFormatado}@s.whatsapp.net`,
          apikey: config.apikey,
          status: detalhesInstancia?.state || 'created',
          serverUrl: config.url,
          instanceId: detalhesInstancia?.instanceId || '',
          integration: {
            token: config.apikey,
            integration: 'WHATSAPP-BAILEYS',
            webhook_wa_business: `http://localhost:8080/webhook/whatsapp/${params.instanceName}`
          },
          profileName: detalhesInstancia?.profileName || '',
          instanceName: params.instanceName,
          apikeywinthor: 'Bearer 8hF4DpG2v3JbT6sQ7kZ5wR1yU0cM9xN8', // Valor padrão conforme exemplo
          profileStatus: detalhesInstancia?.profileStatus || '',
          profilePictureUrl: detalhesInstancia?.profilePictureUrl || ''
        };
        
        // Preparar dados para salvar na tabela infraestrutura_chatbot
        const dadosInfraestrutura = {
          rotaapi: rotaapi,
          codempresa: params.codempresa,
          tipo_integracao: 'smartcobranca',
          api_whatsapp: api_whatsapp,
          apikey_instance: config.apikey,
          instance_name: params.instanceName
        };
        
        console.log('Tentando salvar no banco de dados instância existente:', {
          ...dadosInfraestrutura,
          api_whatsapp: { ...dadosInfraestrutura.api_whatsapp, apikey: '***' } // Ocultar apikey no log
        });
        
        // Salvar na tabela infraestrutura_chatbot
        const { data: savedData, error: saveInfraError } = await supabase
          .from('infraestrutura_chatbot')
          .insert(dadosInfraestrutura)
          .select();
        
        if (saveInfraError) {
          console.error('Erro ao salvar infraestrutura:', JSON.stringify(saveInfraError));
          
          // Verificar se o erro é de chave duplicada
          if (saveInfraError.code === '23505') { // Código de erro para violação de chave única
            return {
              success: true,
              message: 'Instância já existe no banco de dados',
              instanceData: null
            };
          }
          
          return {
            success: true,
            message: `Instância já existe, mas houve um erro ao salvar os dados: ${saveInfraError.message || 'Erro desconhecido'}`,
            instanceData: null
          };
        }
        
        return {
          success: true,
          message: 'Instância já existe e foi registrada no banco de dados',
          instanceData: null
        };
      }
      
      // Obter configuração global da Evolution API
      console.log('Buscando configuração global da Evolution API');
      const config = await this.getConfig();
      
      console.log('Configuração obtida:', {
        url: config?.url || 'URL não encontrada'
      });
      
      if (!config || !config.url || !config.apikey) {
        return {
          success: false,
          message: 'Configuração da Evolution API não encontrada para esta empresa'
        };
      }

      // Validar parâmetros
      if (!params.instanceName || !params.numero) {
        return {
          success: false,
          message: 'Nome da instância e número são obrigatórios'
        };
      }

      // Formatar número (remover caracteres especiais)
      const numeroFormatado = params.numero.replace(/\D/g, '');
      
      // Construir URL completa para criação da instância conforme documentação
      let baseUrl = config.url;
      // Remover qualquer barra no final da URL
      baseUrl = baseUrl.replace(/\/$/, '');
      
      const url = `${baseUrl}/instance/create`;
      
      console.log('URL completa:', url);
      
      // Criar instância com o mínimo de parâmetros possível
      console.log('Tentando criar instância com nome:', params.instanceName);
      
      // Criar instância com parâmetros básicos conforme documentação
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        },
        body: JSON.stringify({
          instanceName: params.instanceName,
          integration: 'WHATSAPP-BAILEYS'
        })
      });
      
      console.log('Status da resposta:', response.status, response.statusText);
      
      // Tentar obter a resposta como texto primeiro para debug
      const responseText = await response.text();
      console.log('Resposta como texto:', responseText);
      
      // Converter para JSON se possível
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Erro ao converter resposta para JSON:', e);
        responseData = { error: 'Resposta inválida da API' };
      }
      
      if (!response.ok) {
        console.error(`Erro ao criar instância: ${response.status} ${response.statusText}`);
        console.error('Detalhes do erro:', responseText);
        
        let mensagemErro = 'Erro desconhecido';
        
        if (responseData && responseData.error) {
          mensagemErro = responseData.error;
        } else if (responseData && responseData.message) {
          if (Array.isArray(responseData.message)) {
            mensagemErro = responseData.message.join(', ');
          } else {
            mensagemErro = responseData.message;
          }
        }
        
        return {
          success: false,
          message: `Erro ao criar instância: ${mensagemErro}`
        };
      }
      
      console.log('Instância criada com sucesso:', responseData);
      
      // Verificar se a instância já existe na tabela infraestrutura_chatbot
      const { data: existingData, error: existingError } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('instance_name', params.instanceName)
        .limit(1);
        
      if (existingData && existingData.length > 0) {
        console.log('Instância já existe na tabela infraestrutura_chatbot:', params.instanceName);
        return {
          success: true,
          message: 'Instância criada com sucesso e já está registrada no banco de dados',
          instanceData: responseData
        };
      }
      
      // Preparar dados para salvar na tabela infraestrutura_chatbot
      const apiWhatsapp = {
        serverUrl: config.url,
        apikey: config.apikey,
        instanceName: params.instanceName,
        numero: numeroFormatado
      };
      
      // Buscar rota da empresa
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('rota')
        .eq('id', params.codempresa)
        .single();
      
      const rotaapi = empresaData?.rota || '';
      
      // Buscar detalhes da instância para obter informações completas
      console.log('Buscando detalhes da instância após criação:', params.instanceName);
      const detalhesInstancia = await this.buscarDetalhesInstancia(params.instanceName);
      
      // Montar objeto api_whatsapp no formato correto
      const api_whatsapp = {
        id: 'smartcobranca', // ID fixo conforme solicitado
        owner: `${numeroFormatado}@s.whatsapp.net`,
        apikey: config.apikey,
        status: detalhesInstancia?.state || 'created',
        serverUrl: config.url,
        instanceId: responseData?.instance?.instanceId || detalhesInstancia?.instanceId || '',
        integration: {
          token: config.apikey,
          integration: 'WHATSAPP-BAILEYS',
          webhook_wa_business: `http://localhost:8080/webhook/whatsapp/${params.instanceName}`
        },
        profileName: detalhesInstancia?.profileName || '',
        instanceName: params.instanceName,
        apikeywinthor: 'Bearer 8hF4DpG2v3JbT6sQ7kZ5wR1yU0cM9xN8', // Valor padrão conforme exemplo
        profileStatus: detalhesInstancia?.profileStatus || '',
        profilePictureUrl: detalhesInstancia?.profilePictureUrl || ''
      };
      
      // Dados para inserir na tabela
      const dadosParaSalvar = {
        rotaapi: rotaapi,
        codempresa: params.codempresa,
        tipo_integracao: 'smartcobranca',
        api_whatsapp: api_whatsapp,
        apikey_instance: config.apikey,
        instance_name: params.instanceName
      };
      
      console.log('Tentando salvar no banco de dados:', {
        ...dadosParaSalvar,
        api_whatsapp: { ...dadosParaSalvar.api_whatsapp, apikey: '***' } // Ocultar apikey no log
      });
      
      // Salvar na tabela infraestrutura_chatbot
      const { data: savedData, error: saveInfraError } = await supabase
        .from('infraestrutura_chatbot')
        .insert(dadosParaSalvar)
        .select();
      
      if (saveInfraError) {
        console.error('Erro ao salvar infraestrutura:', JSON.stringify(saveInfraError));
        
        // Verificar se o erro é de chave duplicada
        if (saveInfraError.code === '23505') { // Código de erro para violação de chave única
          return {
            success: true,
            message: 'Instância já existe no banco de dados',
            instanceData: responseData
          };
        }
        
        return {
          success: true,
          message: `Instância criada com sucesso, mas houve um erro ao salvar os dados: ${saveInfraError.message || 'Erro desconhecido'}`,
          instanceData: responseData
        };
      }
      
      return {
        success: true,
        message: 'Instância criada com sucesso',
        instanceData: responseData
      };
    } catch (err: any) {
      console.error('Erro ao criar instância:', err);
      return {
        success: false,
        message: `Erro ao criar instância: ${err.message || 'Erro desconhecido'}`
      };
    }
  },

  /**
   * Envia mensagem via Evolution API
   */
  /**
   * Obter o QR code para sincronizar com o WhatsApp
   * @param instanceName Nome da instância
   */
  async obterQRCode(instanceName: string): Promise<QRCodeResponse | null> {
    try {
      console.log('Obtendo QR code para a instância:', instanceName);
      
      // Obter configuração global da Evolution API
      const config = await this.getConfig();
      
      if (!config || !config.url || !config.apikey) {
        console.error('Configuração da Evolution API não encontrada');
        return null;
      }
      
      // Construir URL para obter o QR code
      let baseUrl = config.url;
      baseUrl = baseUrl.replace(/\/$/, '');
      
      const url = `${baseUrl}/instance/connect/${instanceName}`;
      
      console.log('URL para obter QR code:', url);
      
      // Fazer requisição para obter o QR code
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        }
      });
      
      if (!response.ok) {
        console.error(`Erro ao obter QR code: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Erro ao converter resposta para JSON:', e);
        return null;
      }
      
      console.log('QR code obtido com sucesso');
      
      return responseData as QRCodeResponse;
    } catch (err) {
      console.error('Erro ao obter QR code:', err);
      return null;
    }
  },
  
  async enviarMensagem(params: MensagemParams): Promise<boolean> {
    try {
      // Buscar configuração da Evolution API
      const config = await this.getConfig();
      if (!config) {
        console.error('Configuração da Evolution API não encontrada');
        return false;
      }

      // Buscar infraestrutura do chatbot
      const infraestrutura = await this.getInfraestruturaChatbot();
      if (!infraestrutura) {
        console.error('Infraestrutura do chatbot não encontrada');
        return false;
      }

      // Formatar telefone (remover caracteres especiais)
      const telefone = params.telefone.replace(/\D/g, '');
      
      // Construir URL para envio de mensagem
      const url = `${config.url}/message/sendText/${infraestrutura.instance_name}`;
      
      // Preparar dados para envio
      const data = {
        number: `${telefone}@s.whatsapp.net`,
        options: {
          delay: 1200,
          presence: 'composing'
        },
        textMessage: {
          text: params.mensagem
        }
      };
      
      console.log('Enviando mensagem via Evolution API:', url);
      console.log('Dados:', data);
      
      // Enviar mensagem
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erro ao enviar mensagem: ${response.status} ${response.statusText}`);
        console.error('Detalhes do erro:', errorText);
        return false;
      }
      
      const result = await response.json();
      console.log('Mensagem enviada com sucesso:', result);
      
      return true;
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      return false;
    }
  }
};
